import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import mt5ServersBundled from '../../data/mt5-servers.json';
import Icon from '../../components/Icons';

const STEPS = [
  { id: 1, label: 'Account Type' },
  { id: 2, label: 'Your Login' },
  { id: 3, label: 'MT5 Account', optional: true },
  { id: 4, label: 'Review' },
];

export default function Register() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    email: '', username: '', password: '', role: 'copier',
    mt5AccountNumber: '', mt5Server: '', mt5Password: '',
    mt5InvestorPassword: false,
  });
  const [broker, setBroker]   = useState('');
  const [manual, setManual]   = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  // Live broker/server list from the backend harvester, with the bundled JSON
  // as the offline/empty fallback so the picker always has something to show.
  const [mt5Servers, setMt5Servers] = useState(mt5ServersBundled);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    api.get('/brokers')
      .then(res => {
        const list = res.data?.servers;
        if (!alive || !Array.isArray(list) || !list.length) return;
        // MERGE harvested servers with the bundled list (don't replace) — the
        // harvester may return only a few (e.g. just the journal-scraped in-use
        // brokers) and we must never show fewer than the curated fallback.
        const byServer = new Map();
        for (const s of mt5ServersBundled) byServer.set(s.server, s);
        for (const s of list) byServer.set(s.server, s); // harvested wins on conflict
        setMt5Servers([...byServer.values()]);
      })
      .catch(() => { /* keep bundled fallback */ });
    return () => { alive = false; };
  }, []);

  const brokers = useMemo(
    () => Array.from(new Set(mt5Servers.map(s => s.broker))).sort(),
    [mt5Servers]
  );
  const serversForBroker = useMemo(
    () => mt5Servers.filter(s => s.broker === broker),
    [mt5Servers, broker]
  );

  function handleChange(e) {
    const { name, type, value, checked } = e.target;
    setForm(prev => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'role' && value !== 'trader') next.mt5InvestorPassword = false;
      return next;
    });
  }

  function pickRole(role) {
    setForm(prev => ({
      ...prev,
      role,
      mt5InvestorPassword: role === 'trader' ? prev.mt5InvestorPassword : false,
    }));
  }

  function handleBrokerChange(e) {
    const next = e.target.value;
    setBroker(next);
    setForm(prev => ({ ...prev, mt5Server: '' }));
  }

  function toggleManual() {
    setManual(prev => !prev);
    setBroker('');
    setForm(prev => ({ ...prev, mt5Server: '' }));
  }

  function validateStep(s) {
    setError('');
    if (s === 1) {
      if (!['copier','trader'].includes(form.role)) {
        setError('Pick an account type to continue.'); return false;
      }
    }
    if (s === 2) {
      if (!form.email) { setError('Email is required.'); return false; }
      if (!/^\S+@\S+\.\S+$/.test(form.email)) { setError('Enter a valid email.'); return false; }
      if (!form.username) { setError('Username is required.'); return false; }
      if (!/^[a-z0-9_.-]{3,24}$/i.test(form.username)) {
        setError('Username must be 3–24 chars: letters, numbers, _ . -');
        return false;
      }
      if (!form.password || form.password.length < 6) {
        setError('Password must be at least 6 characters.'); return false;
      }
    }
    if (s === 3) {
      // The MT5 step is OPTIONAL: an account can be connected later (and more
      // than one) from the MT5 Accounts page. But a partially-filled set of
      // credentials is a mistake, not a choice — all three or none.
      const filled = [form.mt5AccountNumber, form.mt5Server, form.mt5Password].filter(Boolean).length;
      if (filled > 0 && filled < 3) {
        setError('Fill in the MT5 account number, server and password — or leave all three '
          + 'blank and connect an account later.');
        return false;
      }
    }
    return true;
  }

  function next() {
    if (!validateStep(step)) return;
    setStep(s => Math.min(STEPS.length, s + 1));
  }

  // Skip the MT5 step: clear whatever was half-typed and move on. Without the
  // clear, a partially-filled form would fail the all-three-or-none check at
  // submit time with no obvious cause.
  function skipMt5() {
    setError('');
    setBroker('');
    setManual(false);
    setForm(prev => ({
      ...prev,
      mt5AccountNumber: '', mt5Server: '', mt5Password: '', mt5InvestorPassword: false,
    }));
    setStep(4);
  }
  function back() {
    setError('');
    setStep(s => Math.max(1, s - 1));
  }

  async function handleSubmit(e) {
    e?.preventDefault?.();
    // Locked once registered — the 3s pre-navigate window must not allow a
    // second submit ("email already registered" over the success state).
    if (success || loading) return;
    // Enter on an earlier step advances the wizard instead of teleporting to
    // the review step's submit path.
    if (step < STEPS.length) { next(); return; }
    if (!validateStep(3)) { setStep(3); return; }
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/register', form);
      setSuccess(form.mt5AccountNumber
        ? 'Registration successful! Awaiting admin verification.'
        : 'Registration successful! Sign in and connect an MT5 account from your dashboard.');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="wrapper wrapper-full-page">
      <div className="full-page register-page" data-color="black">
        <div className="content">
          <div className="container">
            <div className="col-lg-7 col-md-10 col-sm-12 ml-auto mr-auto">
              <div className="card card-register">
                <div className="card-header text-center">
                  <h3 className="header text-white mb-1">Create Account</h3>
                  <p className="text-white-50 small mb-3">
                    Step {step} of {STEPS.length} — {STEPS[step-1].label}
                    {STEPS[step-1].optional && <span className="text-white-50"> (optional)</span>}
                  </p>
                  <div className="wizard-stepper">
                    {STEPS.map((s, i) => (
                      <React.Fragment key={s.id}>
                        <div className={
                          'wizard-step' +
                          (step === s.id ? ' is-current' : '') +
                          (step > s.id   ? ' is-done'    : '')
                        }>
                          <div className="wizard-step-bubble">
                            {step > s.id ? '✓' : s.id}
                          </div>
                          <div className="wizard-step-label">{s.label}</div>
                        </div>
                        {i < STEPS.length - 1 && (
                          <div className={`wizard-step-line ${step > s.id ? 'is-done' : ''}`} />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                <div className="card-body">
                  {error   && <div className="alert alert-danger">{error}</div>}
                  {success && <div className="alert alert-success">{success}</div>}

                  <form onSubmit={handleSubmit}>

                    {step === 1 && (
                      <div className="wizard-roles">
                        <button
                          type="button"
                          className={`role-card ${form.role === 'copier' ? 'is-selected' : ''}`}
                          onClick={() => pickRole('copier')}
                        >
                          <div className="role-icon"><Icon name="copy" size={28} /></div>
                          <div className="role-title">Copier</div>
                          <div className="role-desc">
                            Mirror trades from experienced traders automatically.
                            You keep full control of risk per relationship.
                          </div>
                        </button>
                        <button
                          type="button"
                          className={`role-card ${form.role === 'trader' ? 'is-selected' : ''}`}
                          onClick={() => pickRole('trader')}
                        >
                          <div className="role-icon"><Icon name="trophy" size={28} /></div>
                          <div className="role-title">Trader</div>
                          <div className="role-desc">
                            Let others copy your trades. Build a following based on
                            your performance and grow your reach.
                          </div>
                        </button>
                      </div>
                    )}

                    {step === 2 && (
                      <>
                        <div className="alert alert-info" style={{ fontSize: 13 }}>
                          <strong>These are NOT your MT5 details.</strong> This is
                          the email &amp; password you'll use to sign in to this
                          platform. You'll enter your MT5 broker credentials in the
                          next step.
                        </div>
                        <div className="form-group">
                          <label className="text-white-50">Email</label>
                          <input type="email" name="email" className="form-control"
                            placeholder="you@example.com" autoFocus
                            value={form.email} onChange={handleChange} required />
                        </div>
                        <div className="form-group">
                          <label className="text-white-50">Username</label>
                          <input
                            type="text" name="username" className="form-control"
                            placeholder="e.g. og_trader"
                            autoCapitalize="off" autoCorrect="off" spellCheck="false"
                            value={form.username}
                            onChange={e => setForm(f => ({
                              ...f,
                              username: e.target.value.toLowerCase().replace(/[^a-z0-9_.-]/g, ''),
                            }))}
                            required
                          />
                          <small className="text-white-50">
                            This is what other traders and copiers will see — not your email.
                            3–24 chars, lowercase letters, numbers, _ . -
                          </small>
                        </div>
                        <div className="form-group">
                          <label className="text-white-50">Password</label>
                          <input type="password" name="password" className="form-control"
                            placeholder="Min 6 characters"
                            value={form.password} onChange={handleChange} required />
                          <small className="text-white-50">
                            Used only to sign in to this platform.
                          </small>
                        </div>
                      </>
                    )}

                    {step === 3 && (
                      <>
                        <div className="alert alert-info" style={{ fontSize: 12.5 }}>
                          <strong>Optional.</strong> You can connect MT5 accounts — and more
                          than one — from your dashboard after signing in. Filling this in now
                          just saves a step.
                        </div>
                        <div className="form-group">
                          <label className="text-white-50">MT5 Account Number</label>
                          <input type="text" name="mt5AccountNumber" className="form-control"
                            placeholder="e.g. 6047164" autoFocus
                            value={form.mt5AccountNumber} onChange={handleChange} />
                        </div>
                        {!manual && (
                          <>
                            <div className="form-group">
                              <label className="text-white-50">Broker</label>
                              <select className="form-control" value={broker} onChange={handleBrokerChange}>
                                <option value="">Select your broker…</option>
                                {brokers.map(b => (
                                  <option key={b} value={b}>{b}</option>
                                ))}
                              </select>
                            </div>
                            <div className="form-group">
                              <label className="text-white-50">MT5 Server</label>
                              <select name="mt5Server" className="form-control"
                                value={form.mt5Server} onChange={handleChange} disabled={!broker}>
                                <option value="">{broker ? 'Select server…' : 'Choose broker first'}</option>
                                {serversForBroker.map(s => (
                                  <option key={s.server} value={s.server}>
                                    {s.server} ({s.type}){s.note ? ` — ${s.note}` : ''}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </>
                        )}
                        {manual && (
                          <div className="form-group">
                            <label className="text-white-50">MT5 Server (manual)</label>
                            <input type="text" name="mt5Server" className="form-control"
                              placeholder="e.g. ICMarketsSC-Live20"
                              value={form.mt5Server} onChange={handleChange} />
                            <small className="text-white-50">
                              Find this on your broker's MT5 setup page.
                            </small>
                          </div>
                        )}
                        <div className="form-group">
                          <button type="button" className="btn btn-link btn-sm text-info p-0"
                            onClick={toggleManual}>
                            {manual ? '← Pick from broker list' : "My broker isn't listed →"}
                          </button>
                        </div>
                        <div className="form-group">
                          <label className="text-white-50">MT5 Password</label>
                          <input type="password" name="mt5Password" className="form-control"
                            placeholder="MT5 account password"
                            value={form.mt5Password} onChange={handleChange} />
                        </div>
                        {form.role === 'trader' && (
                          <div className="form-group" style={{marginTop:-6}}>
                            <label style={{display:'flex',alignItems:'flex-start',gap:8,cursor:'pointer',color:'rgba(255,255,255,0.85)',fontSize:13}}>
                              <input
                                type="checkbox"
                                name="mt5InvestorPassword"
                                checked={form.mt5InvestorPassword}
                                onChange={handleChange}
                                style={{marginTop:3}}
                              />
                              <span>
                                This is an <strong>investor (read-only) password</strong>
                                <div className="text-white-50" style={{fontSize:11.5,marginTop:2}}>
                                  Investor passwords let copiers mirror your trades but you cannot place orders through this platform.
                                </div>
                              </span>
                            </label>
                          </div>
                        )}
                      </>
                    )}

                    {step === 4 && (
                      <div className="wizard-review">
                        <ReviewRow label="Account Type"  value={form.role === 'trader' ? 'Trader' : 'Copier'} onEdit={() => setStep(1)} />
                        <ReviewRow label="Email"         value={form.email}            onEdit={() => setStep(2)} />
                        <ReviewRow label="Username"      value={form.username}         onEdit={() => setStep(2)} />
                        <ReviewRow label="Password"      value={'•'.repeat(Math.min(form.password.length, 10))} onEdit={() => setStep(2)} />
                        <ReviewRow label="MT5 Account"   value={form.mt5AccountNumber || 'Not set — add later'} onEdit={() => setStep(3)} />
                        <ReviewRow label="MT5 Server"    value={form.mt5Server || 'Not set — add later'}        onEdit={() => setStep(3)} />
                        <ReviewRow label="MT5 Password"  value={form.mt5Password ? '•'.repeat(Math.min(form.mt5Password.length, 10)) : 'Not set — add later'} onEdit={() => setStep(3)} />
                        {form.role === 'trader' && form.mt5AccountNumber && (
                          <ReviewRow
                            label="Investor Password"
                            value={form.mt5InvestorPassword ? 'Yes (read-only)' : 'No'}
                            onEdit={() => setStep(3)}
                          />
                        )}
                        <div className="text-white-50 small mt-3">
                          {form.mt5AccountNumber
                            ? 'After registration an admin verifies your account before your MT5 terminal is started. You can connect more MT5 accounts later.'
                            : 'Sign in afterwards and connect an MT5 account from the MT5 Accounts page — an admin verifies each one before its terminal starts.'}
                        </div>
                      </div>
                    )}

                    <div className="wizard-nav">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={back}
                        disabled={step === 1 || loading || !!success}
                      >
                        ← Back
                      </button>
                      {step === 3 && (
                        <button
                          type="button"
                          className="btn btn-link text-white-50"
                          onClick={skipMt5}
                          disabled={loading || !!success}
                          title="Register now and connect an MT5 account from your dashboard later"
                        >
                          Skip for now
                        </button>
                      )}
                      {step < STEPS.length ? (
                        <button type="button" className="btn btn-primary" onClick={next}>
                          Next →
                        </button>
                      ) : (
                        <button type="submit" className="btn btn-primary" disabled={loading || !!success}>
                          {success ? 'Account created ✓' : loading ? 'Creating account...' : 'Create Account'}
                        </button>
                      )}
                    </div>
                  </form>
                </div>
                <div className="card-footer text-center">
                  <Link to="/login" className="text-white">Already have an account? Sign In</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReviewRow({ label, value, onEdit }) {
  return (
    <div className="review-row">
      <div className="review-row-label">{label}</div>
      <div className="review-row-value">{value || <span className="text-muted">—</span>}</div>
      <button type="button" className="btn btn-link btn-sm p-0 text-info" onClick={onEdit}>Edit</button>
    </div>
  );
}
